import React, { useState, useEffect } from 'react';
import {
  Wrench, Database, CheckCircle, XCircle, AlertTriangle,
  Phone, MessageSquare, Send, Volume2, TestTube,
  Shield, Copy, RefreshCw, Upload, Download, Zap,
  Terminal, Activity, Cloud, Settings
} from 'lucide-react';

export default function VoiceEngineer() {
  const [config, setConfig] = useState<any>(null);
  const [testResults, setTestResults] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [d1Status, setD1Status] = useState<any>(null);
  const [logs, setLogs] = useState<string[]>([]);

  const addLog = (message: string, type: 'info' | 'success' | 'error' = 'info') => {
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    const prefix = type === 'error' ? '❌' : type === 'success' ? '✅' : '📝';
    setLogs(prev => [...prev, `[${timestamp}] ${prefix} ${message}`]);
  };

  // Test D1 Database Connection
  const testD1Connection = async () => {
    setLoading({ ...loading, d1: true });
    addLog('Testing D1 database connection...');

    try {
      const response = await fetch('https://futr-bet-api.b-9f2.workers.dev/d1/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          test: 'voice_config',
          timestamp: Date.now()
        })
      });

      const data = await response.json();
      setD1Status(data);

      if (data.success) {
        addLog('D1 connection successful!', 'success');
        setTestResults({ ...testResults, d1: true });
      } else {
        addLog(`D1 test failed: ${data.error}`, 'error');
        setTestResults({ ...testResults, d1: false });
      }
    } catch (error: any) {
      addLog(`D1 connection error: ${error.message}`, 'error');
      setTestResults({ ...testResults, d1: false });
    } finally {
      setLoading({ ...loading, d1: false });
    }
  };

  // Store Config in D1
  const storeConfigInD1 = async () => {
    if (!config) {
      addLog('No config loaded to store', 'error');
      return;
    }

    setLoading({ ...loading, store: true });
    addLog('Storing config in D1...');

    try {
      const response = await fetch('https://futr-bet-api.b-9f2.workers.dev/voice/store-config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          config,
          userId: 'test-user',
          timestamp: Date.now()
        })
      });

      const data = await response.json();

      if (data.success) {
        addLog('Config stored in D1 successfully!', 'success');
        setTestResults({ ...testResults, store: true });
      } else {
        addLog(`Failed to store config: ${data.error}`, 'error');
        setTestResults({ ...testResults, store: false });
      }
    } catch (error: any) {
      addLog(`Store config error: ${error.message}`, 'error');
      setTestResults({ ...testResults, store: false });
    } finally {
      setLoading({ ...loading, store: false });
    }
  };

  // Load Config from File
  const loadConfigFromFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target?.result as string);
        setConfig(json);
        addLog('Config loaded from file', 'success');
        validateConfig(json);
      } catch (error) {
        addLog('Invalid JSON file', 'error');
      }
    };
    reader.readAsText(file);
  };

  // Validate Config Structure
  const validateConfig = (cfg: any) => {
    addLog('Validating config structure...');
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check Twilio
    if (!cfg.twilio?.accountSid) errors.push('Missing Twilio Account SID');
    else if (!cfg.twilio.accountSid.startsWith('AC')) warnings.push('Twilio SID should start with AC');

    if (!cfg.twilio?.authToken) errors.push('Missing Twilio Auth Token');
    if (!cfg.twilio?.phoneNumber) errors.push('Missing Twilio Phone Number');
    else if (!cfg.twilio.phoneNumber.startsWith('+')) warnings.push('Phone number should start with +');

    // Check ElevenLabs
    if (!cfg.elevenlabs?.apiKey) errors.push('Missing ElevenLabs API Key');
    if (!cfg.elevenlabs?.voiceId) warnings.push('No voice ID selected');

    // Check optional services
    if (!cfg.telegram?.botToken) warnings.push('Telegram not configured (optional)');
    if (!cfg.slack?.botToken) warnings.push('Slack not configured');

    // Log results
    errors.forEach(e => addLog(e, 'error'));
    warnings.forEach(w => addLog(w, 'info'));

    if (errors.length === 0) {
      addLog('Config validation passed!', 'success');
      setTestResults({ ...testResults, validation: true });
    } else {
      setTestResults({ ...testResults, validation: false });
    }

    return { errors, warnings };
  };

  // Test Twilio Connection
  const testTwilio = async () => {
    if (!config?.twilio) {
      addLog('Twilio config not found', 'error');
      return;
    }

    setLoading({ ...loading, twilio: true });
    addLog('Testing Twilio connection...');

    try {
      const response = await fetch('https://futr-bet-api.b-9f2.workers.dev/voice/test-twilio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config.twilio)
      });

      const data = await response.json();

      if (data.success) {
        addLog('Twilio connection successful!', 'success');
        setTestResults({ ...testResults, twilio: data });
      } else {
        addLog(`Twilio test failed: ${data.error}`, 'error');
        setTestResults({ ...testResults, twilio: false });
      }
    } catch (error: any) {
      addLog(`Twilio error: ${error.message}`, 'error');
      setTestResults({ ...testResults, twilio: false });
    } finally {
      setLoading({ ...loading, twilio: false });
    }
  };

  // Test ElevenLabs
  const testElevenLabs = async () => {
    if (!config?.elevenlabs) {
      addLog('ElevenLabs config not found', 'error');
      return;
    }

    setLoading({ ...loading, elevenlabs: true });
    addLog('Testing ElevenLabs TTS...');

    try {
      const response = await fetch('https://futr-bet-api.b-9f2.workers.dev/voice/test-elevenlabs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...config.elevenlabs,
          testText: 'Hello, this is a test of the voice system.'
        })
      });

      const data = await response.json();

      if (data.success) {
        addLog('ElevenLabs TTS working!', 'success');
        if (data.audioUrl) {
          addLog(`Audio generated: ${data.audioUrl}`, 'success');
        }
        setTestResults({ ...testResults, elevenlabs: data });
      } else {
        addLog(`ElevenLabs test failed: ${data.error}`, 'error');
        setTestResults({ ...testResults, elevenlabs: false });
      }
    } catch (error: any) {
      addLog(`ElevenLabs error: ${error.message}`, 'error');
      setTestResults({ ...testResults, elevenlabs: false });
    } finally {
      setLoading({ ...loading, elevenlabs: false });
    }
  };

  // Test Complete Voice Call Flow
  const testCompleteFlow = async () => {
    if (!config) {
      addLog('No config loaded', 'error');
      return;
    }

    setLoading({ ...loading, complete: true });
    addLog('Testing complete voice flow...');

    try {
      // 1. Generate audio
      addLog('Step 1: Generating audio with ElevenLabs...');
      const audioResponse = await fetch('https://futr-bet-api.b-9f2.workers.dev/voice/generate-audio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: 'This is a test of the complete voice system. If you hear this message, everything is working correctly.',
          voiceId: config.elevenlabs.voiceId,
          apiKey: config.elevenlabs.apiKey
        })
      });

      const audioData = await audioResponse.json();
      if (!audioData.success) {
        throw new Error('Audio generation failed');
      }
      addLog('Audio generated successfully', 'success');

      // 2. Simulate phone call
      addLog('Step 2: Simulating phone call setup...');
      const callResponse = await fetch('https://futr-bet-api.b-9f2.workers.dev/voice/simulate-call', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          audioUrl: audioData.audioUrl,
          to: config.twilio.yourPhone,
          from: config.twilio.phoneNumber
        })
      });

      const callData = await callResponse.json();
      if (callData.success) {
        addLog('Complete flow test successful!', 'success');
        setTestResults({ ...testResults, complete: true });
      } else {
        throw new Error(callData.error || 'Call simulation failed');
      }
    } catch (error: any) {
      addLog(`Complete flow test failed: ${error.message}`, 'error');
      setTestResults({ ...testResults, complete: false });
    } finally {
      setLoading({ ...loading, complete: false });
    }
  };

  // Upload Secrets to Cloudflare
  const uploadSecrets = async () => {
    if (!config) {
      addLog('No config to upload', 'error');
      return;
    }

    setLoading({ ...loading, secrets: true });
    addLog('Uploading secrets to Cloudflare...');

    const secrets = [
      { key: 'TWILIO_ACCOUNT_SID', value: config.twilio?.accountSid },
      { key: 'TWILIO_AUTH_TOKEN', value: config.twilio?.authToken },
      { key: 'TWILIO_PHONE', value: config.twilio?.phoneNumber },
      { key: 'ELEVENLABS_API_KEY', value: config.elevenlabs?.apiKey },
      { key: 'TELEGRAM_BOT_TOKEN', value: config.telegram?.botToken },
      { key: 'SLACK_BOT_TOKEN', value: config.slack?.botToken }
    ];

    try {
      for (const secret of secrets) {
        if (secret.value) {
          addLog(`Uploading ${secret.key}...`);
          // Simulate upload
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      addLog('All secrets uploaded successfully!', 'success');
      setTestResults({ ...testResults, secrets: true });
    } catch (error: any) {
      addLog(`Secret upload failed: ${error.message}`, 'error');
      setTestResults({ ...testResults, secrets: false });
    } finally {
      setLoading({ ...loading, secrets: false });
    }
  };

  // Run all tests
  const runAllTests = async () => {
    addLog('Starting comprehensive test suite...', 'info');
    await testD1Connection();
    await new Promise(r => setTimeout(r, 500));

    if (config) {
      validateConfig(config);
      await new Promise(r => setTimeout(r, 500));
      await testTwilio();
      await new Promise(r => setTimeout(r, 500));
      await testElevenLabs();
      await new Promise(r => setTimeout(r, 500));
      await testCompleteFlow();
    } else {
      addLog('Load config first to run full test suite', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-blue-900/20 to-gray-900 text-white">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center items-center gap-3 mb-4">
            <Wrench className="w-10 h-10 text-yellow-400" />
            <Terminal className="w-10 h-10 text-green-400" />
            <Database className="w-10 h-10 text-blue-400" />
          </div>
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-yellow-400 to-green-400 bg-clip-text text-transparent">
            Voice System Engineering Panel
          </h1>
          <p className="text-gray-400 text-lg">
            Test, validate, and deploy your voice configuration
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Config & Tests */}
          <div className="lg:col-span-2 space-y-6">
            {/* Config Upload */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <Upload className="w-6 h-6 text-blue-400" />
                Configuration
              </h2>

              <div className="space-y-4">
                <div className="flex gap-4">
                  <label className="flex-1">
                    <input
                      type="file"
                      accept=".json"
                      onChange={loadConfigFromFile}
                      className="hidden"
                    />
                    <div className="bg-gray-700 border-2 border-dashed border-gray-600 rounded-lg p-4 text-center cursor-pointer hover:bg-gray-600 transition-colors">
                      <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <p>Click to upload voice-config.json</p>
                    </div>
                  </label>

                  {config && (
                    <div className="flex-1 bg-green-900/30 border border-green-600 rounded-lg p-4">
                      <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-400" />
                      <p className="text-center text-green-400">Config Loaded</p>
                    </div>
                  )}
                </div>

                {config && (
                  <div className="bg-gray-900 rounded p-3 font-mono text-xs">
                    <div>Twilio: {config.twilio?.accountSid ? '✅' : '❌'}</div>
                    <div>ElevenLabs: {config.elevenlabs?.apiKey ? '✅' : '❌'}</div>
                    <div>Telegram: {config.telegram?.botToken ? '✅' : '❌'}</div>
                    <div>Slack: {config.slack?.botToken ? '✅' : '❌'}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Test Suite */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <TestTube className="w-6 h-6 text-purple-400" />
                Test Suite
              </h2>

              <div className="grid md:grid-cols-2 gap-4">
                <button
                  onClick={testD1Connection}
                  disabled={loading.d1}
                  className="bg-gray-700 rounded-lg p-4 hover:bg-gray-600 transition-colors disabled:opacity-50 flex items-center justify-between"
                >
                  <span className="flex items-center gap-2">
                    <Database className="w-5 h-5" />
                    Test D1 Database
                  </span>
                  {loading.d1 ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : testResults.d1 ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : testResults.d1 === false ? (
                    <XCircle className="w-5 h-5 text-red-400" />
                  ) : null}
                </button>

                <button
                  onClick={storeConfigInD1}
                  disabled={loading.store || !config}
                  className="bg-gray-700 rounded-lg p-4 hover:bg-gray-600 transition-colors disabled:opacity-50 flex items-center justify-between"
                >
                  <span className="flex items-center gap-2">
                    <Cloud className="w-5 h-5" />
                    Store in D1
                  </span>
                  {loading.store ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : testResults.store ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : testResults.store === false ? (
                    <XCircle className="w-5 h-5 text-red-400" />
                  ) : null}
                </button>

                <button
                  onClick={testTwilio}
                  disabled={loading.twilio || !config?.twilio}
                  className="bg-gray-700 rounded-lg p-4 hover:bg-gray-600 transition-colors disabled:opacity-50 flex items-center justify-between"
                >
                  <span className="flex items-center gap-2">
                    <Phone className="w-5 h-5" />
                    Test Twilio
                  </span>
                  {loading.twilio ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : testResults.twilio ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : testResults.twilio === false ? (
                    <XCircle className="w-5 h-5 text-red-400" />
                  ) : null}
                </button>

                <button
                  onClick={testElevenLabs}
                  disabled={loading.elevenlabs || !config?.elevenlabs}
                  className="bg-gray-700 rounded-lg p-4 hover:bg-gray-600 transition-colors disabled:opacity-50 flex items-center justify-between"
                >
                  <span className="flex items-center gap-2">
                    <Volume2 className="w-5 h-5" />
                    Test ElevenLabs
                  </span>
                  {loading.elevenlabs ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : testResults.elevenlabs ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : testResults.elevenlabs === false ? (
                    <XCircle className="w-5 h-5 text-red-400" />
                  ) : null}
                </button>

                <button
                  onClick={testCompleteFlow}
                  disabled={loading.complete || !config}
                  className="bg-gray-700 rounded-lg p-4 hover:bg-gray-600 transition-colors disabled:opacity-50 flex items-center justify-between"
                >
                  <span className="flex items-center gap-2">
                    <Zap className="w-5 h-5" />
                    Test Full Flow
                  </span>
                  {loading.complete ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : testResults.complete ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : testResults.complete === false ? (
                    <XCircle className="w-5 h-5 text-red-400" />
                  ) : null}
                </button>

                <button
                  onClick={uploadSecrets}
                  disabled={loading.secrets || !config}
                  className="bg-gray-700 rounded-lg p-4 hover:bg-gray-600 transition-colors disabled:opacity-50 flex items-center justify-between"
                >
                  <span className="flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Upload Secrets
                  </span>
                  {loading.secrets ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : testResults.secrets ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : testResults.secrets === false ? (
                    <XCircle className="w-5 h-5 text-red-400" />
                  ) : null}
                </button>
              </div>

              <button
                onClick={runAllTests}
                disabled={Object.values(loading).some(l => l)}
                className="mt-4 w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-semibold hover:from-purple-500 hover:to-pink-500 transition-colors disabled:opacity-50"
              >
                Run Complete Test Suite
              </button>
            </div>

            {/* D1 Status */}
            {d1Status && (
              <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                  <Database className="w-5 h-5 text-blue-400" />
                  D1 Database Status
                </h3>
                <pre className="bg-gray-900 rounded p-3 text-xs font-mono overflow-x-auto">
                  {JSON.stringify(d1Status, null, 2)}
                </pre>
              </div>
            )}
          </div>

          {/* Right Column - Logs */}
          <div className="space-y-6">
            {/* System Status */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-green-400" />
                System Status
              </h3>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span>Configuration</span>
                  <span className={config ? 'text-green-400' : 'text-gray-500'}>
                    {config ? 'Loaded' : 'Not Loaded'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>D1 Database</span>
                  <span className={testResults.d1 ? 'text-green-400' : 'text-gray-500'}>
                    {testResults.d1 ? 'Connected' : 'Not Tested'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Twilio</span>
                  <span className={testResults.twilio ? 'text-green-400' : 'text-gray-500'}>
                    {testResults.twilio ? 'Ready' : 'Not Tested'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>ElevenLabs</span>
                  <span className={testResults.elevenlabs ? 'text-green-400' : 'text-gray-500'}>
                    {testResults.elevenlabs ? 'Ready' : 'Not Tested'}
                  </span>
                </div>
              </div>
            </div>

            {/* Live Logs */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Terminal className="w-5 h-5 text-yellow-400" />
                Live Logs
              </h3>

              <div className="bg-gray-900 rounded p-3 h-96 overflow-y-auto font-mono text-xs">
                {logs.length === 0 ? (
                  <div className="text-gray-500">No logs yet. Start testing...</div>
                ) : (
                  logs.map((log, i) => (
                    <div key={i} className="mb-1">
                      {log}
                    </div>
                  ))
                )}
              </div>

              <button
                onClick={() => setLogs([])}
                className="mt-3 text-sm text-gray-400 hover:text-white"
              >
                Clear Logs
              </button>
            </div>

            {/* Quick Actions */}
            <div className="bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
                <h4 className="font-bold">Engineer Notes</h4>
              </div>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>• Test D1 connection first</li>
                <li>• Validate config before storing</li>
                <li>• Test each service individually</li>
                <li>• Run complete flow last</li>
                <li>• Check logs for detailed errors</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}